import { useEffect } from 'react'
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, IconButton, Autocomplete, Box,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createClientRequest, updateClientRequest, getClientRequest,
  type ClientRequestListItem, type SaveClientRequestPayload,
} from '../../api/cereri'
import { listClients, getClient } from '../../api/clienti'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const lineSchema = z.object({
  articleId: z.string().nullable(),
  freeTextDescription: z.string().optional(),
  quantityThousands: z.string().min(1, 'Câmp obligatoriu'),
})
const schema = z.object({
  requestedDate: z.string().min(1, 'Câmp obligatoriu'),
  clientId: z.string().min(1, 'Selectați un client'),
  contactId: z.string().nullable(),
  source: z.enum(['Telefon', 'Email', 'Vizita']),
  notes: z.string().optional(),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  requestedDate: new Date().toISOString().slice(0, 10), clientId: '', contactId: null,
  source: 'Telefon', notes: '', lines: [],
}

export default function ClientRequestFormDialog({ target, onClose }: { target: ClientRequestListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toti'], queryFn: () => listClients(), enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['comercial', 'cereri', editing ? (target as ClientRequestListItem).id : null],
    queryFn: () => getClientRequest((target as ClientRequestListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })
  const clientId = useWatch({ control, name: 'clientId' })

  const { data: clientDetail } = useQuery({
    queryKey: ['comercial', 'client', clientId || null], queryFn: () => getClient(clientId), enabled: !!clientId,
  })

  useEffect(() => {
    if (editing && detail) {
      reset({
        requestedDate: detail.requestedDate, clientId: detail.clientId, contactId: detail.contactId,
        source: detail.source, notes: detail.notes ?? '',
        lines: detail.lines.map((l) => ({ articleId: l.articleId, freeTextDescription: l.freeTextDescription ?? '', quantityThousands: String(l.quantityThousands) })),
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveClientRequestPayload = {
        requestedDate: values.requestedDate, clientId: values.clientId, contactId: values.contactId || null,
        source: values.source, notes: values.notes || null,
        lines: values.lines.map((l) => ({
          articleId: l.articleId, freeTextDescription: l.freeTextDescription || null, quantityThousands: Number(l.quantityThousands),
        })),
      }
      if (editing) return updateClientRequest((target as ClientRequestListItem).id, payload)
      return createClientRequest(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'cereri'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare cerere client' : 'Cerere client nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="requestedDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data cererii" sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.requestedDate} helperText={errors.requestedDate?.message} />
              )} />
              <Controller name="clientId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={clients?.map((c) => c.id) ?? []}
                  getOptionLabel={(id) => clients?.find((c) => c.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Client" error={!!errors.clientId} helperText={errors.clientId?.message} />}
                />
              )} />
              <Controller name="source" control={control} render={({ field }) => (
                <TextField {...field} select label="Canal" sx={{ width: 150 }} error={!!errors.source} helperText={errors.source?.message}>
                  <MenuItem value="Telefon">Telefon</MenuItem>
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="Vizita">Vizită</MenuItem>
                </TextField>
              )} />
            </Stack>
            <Controller name="contactId" control={control} render={({ field }) => (
              <Autocomplete
                options={clientDetail?.contacts.map((c) => c.id!) ?? []}
                getOptionLabel={(id) => clientDetail?.contacts.find((c) => c.id === id)?.name ?? id}
                value={field.value} onChange={(_, v) => field.onChange(v)}
                renderInput={(params) => <TextField {...params} label="Persoană de contact (opțional)" />}
              />
            )} />
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Note" fullWidth multiline minRows={2} />
            )} />

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`lines.${i}.articleId`} control={control} render={({ field }) => (
                  <Autocomplete
                    sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
                    getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                    value={field.value} onChange={(_, v) => field.onChange(v)}
                    renderInput={(params) => <TextField {...params} label="Articol (dacă există)" />}
                  />
                )} />
                <Controller name={`lines.${i}.freeTextDescription`} control={control} render={({ field }) => (
                  <TextField {...field} label="Descriere produs custom" sx={{ flexGrow: 1 }} />
                )} />
                <Controller name={`lines.${i}.quantityThousands`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Cant. (mii buc)" sx={{ width: 150 }}
                    error={!!errors.lines?.[i]?.quantityThousands} helperText={errors.lines?.[i]?.quantityThousands?.message} />
                )} />
                <IconButton onClick={() => lines.remove(i)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Box>
              <Button size="small" startIcon={<Add />} onClick={() => lines.append({ articleId: null, freeTextDescription: '', quantityThousands: '' })}>
                Adaugă linie
              </Button>
            </Box>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
